import '../styles/adminResponsive.css';
import { safeLocalStorage } from '../lib/safeStorage';
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { KeyRound, ShieldCheck, Eye, EyeOff, Trash2, Save } from 'lucide-react';
import { AdminAiUsage } from '../components/AdminAiUsage';
import MembershipPaymentSettings from '../components/MembershipPaymentSettings';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({
  headers: { Authorization: `Bearer ${safeLocalStorage.getItem('mm_access_token')}` }
});

const KeyStatus = ({ status }) => (
  <div className="flex items-center gap-2 text-sm admin-settings-key-status" data-testid="openai-key-status">
    <span className={`w-2 h-2 rounded-full ${status.configured ? 'bg-emerald-500' : 'bg-zinc-300'}`} />
    {status.configured
      ? <span className="text-zinc-700">Kayıtlı: <span className="font-mono text-zinc-900" data-testid="openai-key-masked">{status.masked}</span></span>
      : <span className="text-zinc-500">Henüz anahtar kaydedilmedi</span>}
    <span className="ml-auto text-xs text-zinc-400">{status.provider ? `${status.provider} · ` : ""}<span className="font-mono">{status.model}</span></span>
  </div>
);

const AdminSettings = () => {
  const [status, setStatus] = useState({ configured: false, masked: '', model: '' });
  const [value, setValue] = useState('');
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/settings/openai-key`, authHeaders());
      setStatus(data);
    } catch { toast.error('Ayar durumu alınamadı'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    if (!value.trim()) return;
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/api/admin/settings/openai-key`, { api_key: value.trim() }, authHeaders());
      setStatus(s => ({ ...s, ...data }));
      setValue('');
      setShow(false);
      if (data.persisted === false) toast.warning('Bu değişiklik yalnızca mevcut çalışan instance için geçerli. Kalıcı değişiklik Render Environment üzerinden yapılmalı.', { duration: 8000 });
      else toast.success('OpenAI anahtarı güvenli şekilde kaydedildi');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Anahtar kaydedilemedi');
    } finally { setSaving(false); }
  };

  const clear = async () => {
    if (!window.confirm('OpenAI anahtarı silinsin mi? AI Asistan çalışmayı durdurur.')) return;
    try {
      const { data } = await axios.delete(`${API}/api/admin/settings/openai-key`, authHeaders());
      setStatus(s => ({ ...s, ...data }));
      if (data.persisted === false) toast.warning('Bu değişiklik yalnızca mevcut çalışan instance için geçerli. Kalıcı değişiklik Render Environment üzerinden yapılmalı.', { duration: 8000 });
      else toast.success('Anahtar silindi');
    } catch { toast.error('Anahtar silinemedi'); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10 admin-responsive-page" data-testid="admin-settings-page">
      <div className="max-w-2xl">
      <h1 className="text-3xl font-serif font-semibold tracking-tight text-zinc-900 mb-1">Ayarlar</h1>
      <p className="text-sm text-zinc-500 mb-8">Gizli anahtarlar yalnızca sunucu ortam değişkenlerinde saklanır; tam değer hiçbir zaman geri gösterilmez.</p>

      <section className="bg-white border border-zinc-100 rounded-2xl p-6 space-y-5 admin-responsive-card">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#E6EEFF] flex items-center justify-center">
            <KeyRound size={18} strokeWidth={1.75} className="text-[#1E3A8A]" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-zinc-900">OpenAI API Anahtarı</h2>
            <p className="text-xs text-zinc-500">AI Etkinlik Asistanı (Responses API + web_search) için kullanılır</p>
          </div>
        </div>

        <KeyStatus status={status} />

        <form onSubmit={save} className="space-y-3">
          <label className="block text-xs font-medium text-zinc-600" htmlFor="openai-key-input">
            {status.configured ? 'Yeni anahtar (mevcut anahtarın üzerine yazar)' : 'Anahtar'}
          </label>
          <div className="relative">
            <input
              id="openai-key-input"
              data-testid="openai-key-input"
              type={show ? 'text' : 'password'}
              autoComplete="off"
              spellCheck={false}
              value={value}
              onChange={e => setValue(e.target.value)}
              placeholder="sk-..."
              className="w-full h-11 rounded-xl border border-zinc-200 bg-zinc-50 px-3.5 pr-11 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30 focus:border-[#1E3A8A]"
            />
            <button type="button" onClick={() => setShow(s => !s)} data-testid="openai-key-toggle-visibility"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                    aria-label={show ? 'Gizle' : 'Göster'}>
              {show ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button type="submit" disabled={saving || !value.trim()} data-testid="openai-key-save-button"
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-[#1E3A8A] text-white text-sm font-medium disabled:opacity-50 hover:bg-[#16307A] transition-colors">
              <Save size={15} /> {saving ? 'Kaydediliyor…' : 'Kaydet'}
            </button>
            {status.configured && (
              <button type="button" onClick={clear} data-testid="openai-key-clear-button"
                      className="inline-flex items-center gap-2 h-10 px-4 rounded-xl border border-zinc-200 text-zinc-600 text-sm hover:bg-zinc-50 transition-colors">
                <Trash2 size={15} /> Sil
              </button>
            )}
          </div>
        </form>

        <div className="flex items-start gap-2 text-xs text-zinc-500 bg-[#F8FAFF] border border-[#E6EEFF] rounded-xl p-3">
          <ShieldCheck size={14} className="text-[#1E3A8A] shrink-0 mt-0.5" />
          <span>Production'da kalıcı kaynak <code className="font-mono">OPENAI_API_KEY</code> environment variable'ıdır (Render Environment). Buradan girilen anahtar anında etkinleşir; ancak yalnızca çalışan instance için geçerli olabilir.</span>
        </div>
      </section>
      <div className="mt-8"><MembershipPaymentSettings /></div>
      </div>

      <div className="mt-8"><AdminAiUsage /></div>
    </div>
  );
};

export default AdminSettings;
